import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { SignedIn, SignedOut, SignInButton, UserButton, useUser } from "@clerk/clerk-react";
import { Link } from 'react-router-dom'; 
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '../supabaseClient';
import "./Newsletter.css";
import "../App.css";
import { ToastContainer, toast } from "react-toastify";

export default function NewsletterPage() {
  const [selectedImageAd, setSelectedImageAd] = useState("");
  const [selectedVideoAd, setSelectedVideoAd] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [question, setQuestion] = useState('');
  const [llmResponse, setLlmResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [meterData, setMeterData] = useState([]);
  const [maxRecords] = useState(20);
  const [searchInfo, setSearchInfo] = useState(null);
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [graphsLoading, setGraphsLoading] = useState(true);
  const [availableMeters, setAvailableMeters] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Configuration - change this email to receive submissions
  const RECIPIENT_EMAIL = "alex@azzotto.com";
  
  // Meter configuration from App.js
  const METER_CONFIG = {
    "SMR-43563-2-A": {
      displayName: "EDF Energy - Meter SMR-43563-2-A",
      supplier: "EDF Energy",
      color: '#8884d8'
    },
    "SMR-98756-1-A": {
      displayName: "Octopus Energy - Meter SMR-98756-1-A",
      supplier: "Octopus Energy",
      color: '#ff8042'
    },
    "SMR-65228-1-B": {
      displayName: "E.ON Next - Meter SMR-65228-1-B",
      supplier: "E.ON Next",
      color: '#0088fe'
    }
  };
  
  // Fetch meter data for visualization
  useEffect(() => {
    fetchMeterData();
    const interval = setInterval(fetchMeterData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Add this function for sample data
  const generateSampleData = () => {
    const data = [];
    const now = new Date();
    
    for (let i = 0; i < 10; i++) {
      const time = new Date(now.getTime() - (10 - i) * 60000); // Last 10 minutes
      const dataPoint = { time: time.toLocaleTimeString() };
      
      // Add data for each configured meter
      Object.keys(METER_CONFIG).forEach(meterId => {
        dataPoint[meterId] = Math.random() * 100 + 50;
      });
      
      data.push(dataPoint);
    }
    return data;
  };

  const fetchMeterData = async () => {
    try {
      setGraphsLoading(true);
      console.log('Fetching meter data...');
      
      // Now fetch the actual readings for configured meters
      const { data, error } = await supabase
        .from('readings')
        .select('*')
        .in('meter_id', Object.keys(METER_CONFIG))
        .order('timestamp', { ascending: true })
        .limit(500);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log('Fetched meter data count:', data.length);
        
        // Debug: Check what meters we actually got data for
        const metersWithData = [...new Set(data.map(item => item.meter_id))];
        console.log('Meters with data in this fetch:', metersWithData);
        
        const processedData = processMeterData(data);
        console.log('Processed data points:', processedData.length);
        
        if (processedData.length > 0) {
          // Check if we have data for all expected meters
          const firstDataPoint = processedData[0];
          const availableMeterKeys = Object.keys(firstDataPoint).filter(key => key !== 'time');
          console.log('Available meter keys in processed data:', availableMeterKeys);
          
          // Debug: Show sample data for each meter
          availableMeterKeys.forEach(key => {
            const sampleValues = processedData.slice(0, 3).map(item => item[key]);
            console.log(`Sample data for ${key}:`, sampleValues);
          });
        }
        
        setMeterData(processedData);
        setAvailableMeters(metersWithData);
      } else {
        console.log('No meter data found, using sample data');
        setMeterData(generateSampleData());
        setAvailableMeters(Object.keys(METER_CONFIG));
      }
    } catch (error) {
      console.error('Error fetching meter data:', error);
      setMeterData(generateSampleData());
      setAvailableMeters(Object.keys(METER_CONFIG));
    } finally {
      setGraphsLoading(false);
    }
  };

  // FIXED: Process data using actual meter IDs from configuration
  const processMeterData = (data) => {
    if (!data || data.length === 0) return [];

    console.log('Processing meter data, total readings:', data.length);
    
    // Group by timestamp and include all meter readings at that timestamp
    const timeGroups = {};
    
    data.forEach(reading => {
      if (!reading.timestamp || reading.reading === null || reading.reading === undefined) return;
      
      const timestamp = new Date(reading.timestamp).toLocaleTimeString();
      if (!timeGroups[timestamp]) {
        timeGroups[timestamp] = { time: timestamp };
      }
      
      // Use the actual meter ID as the data key
      timeGroups[timestamp][reading.meter_id] = parseFloat(reading.reading);
    });

    // Convert to array and sort by time
    const result = Object.values(timeGroups).sort((a, b) => {
      return new Date(`1970-01-01T${a.time}`) - new Date(`1970-01-01T${b.time}`);
    });
    
    // Fill missing meter values with null to maintain consistent data structure
    const finalResult = result.map(item => {
      Object.keys(METER_CONFIG).forEach(meterId => {
        if (!(meterId in item)) {
          item[meterId] = null;
        }
      });
      return item;
    });

    console.log('Final processed data structure sample:', finalResult.slice(0, 3));
    console.log('All meter keys available:', finalResult.length > 0 ? Object.keys(finalResult[0]) : 'No data');
    
    // Debug: Count non-null values for each meter
    if (finalResult.length > 0) {
      Object.keys(METER_CONFIG).forEach(meterId => {
        const nonNullCount = finalResult.filter(item => item[meterId] !== null).length;
        console.log(`Meter ${meterId}: ${nonNullCount} non-null readings`);
      });
    }
    
    return finalResult;
  };

  // Get meter display name based on ID
  const getMeterDisplayName = (meterId) => {
    return METER_CONFIG[meterId]?.displayName || `Meter ${meterId}`;
  };

  // Chart colors for different meters
  const getMeterColor = (meterId) => {
    return METER_CONFIG[meterId]?.color || '#8884d8';
  };

  // Custom tooltip for better display
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <p className="label" style={{ marginBottom: '5px', fontWeight: 'bold' }}>
            {`Time: ${label}`}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ 
              color: entry.color, 
              margin: '2px 0',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px'
            }}>
              <span>{getMeterDisplayName(entry.dataKey)}:</span>
              <span style={{ fontWeight: 'bold' }}>
                {entry.value ? `${entry.value.toFixed(2)} kWh` : 'N/A'}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const onSubmit = (data) => {
    console.log("Form submitted:", data);
    console.log(`This would be sent to: ${RECIPIENT_EMAIL}`);
    
    // Show success toast
    toast.success("Thank you for subscribing to our newsletter!");
    
    // In a real implementation, you would send this data to your backend
    // which would then handle the email distribution
  };

  // ENHANCED: Updated LLM endpoint with semantic search support
  const handleAskJim = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setLlmResponse('');
    setSearchInfo(null);
    setSimilarQuestions([]);

    try {
      // Health check - use port 5000 for LLM service
      try {
        const healthCheck = await fetch('http://localhost:5000/health', {
          method: 'GET',
        });
        
        if (!healthCheck.ok) {
          throw new Error('LLM service is not available');
        }
        
        const healthData = await healthCheck.json();
        console.log('LLM Service Health:', healthData);
        
      } catch (healthError) {
        throw new Error('LLM service is not available. Please make sure the backend server is running on port 5000.');
      }

      // Call enhanced LLM service with semantic search
      console.log('Sending query to LLM service:', question);
      const response = await fetch('http://localhost:5000/api/llm/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question,
          maxRecords: maxRecords
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('LLM Response:', result);

      if (result.success) {
        setLlmResponse(result.answer);
        
        // Set semantic search information
        if (result.semanticSearchUsed || result.searchType) {
          setSearchInfo({
            type: result.searchType || 'semantic',
            similarQuestions: result.similarQuestions || [],
            timestamp: result.timestamp
          });
        }
        
        if (result.similarQuestions && result.similarQuestions.length > 0) {
          setSimilarQuestions(result.similarQuestions);
        }
        
        await saveToEnergyAnalyses(question, result.answer);
        
        // Show success toast with search type
        const searchType = result.searchType === 'vector_search' ? 'Vector Semantic Search' : 
                          result.searchType === 'keyword_search' ? 'Enhanced Keyword Search' : 'AI Analysis';
        toast.success(`Analysis complete using ${searchType}!`);
        
      } else {
        setLlmResponse('Error: ' + (result.error || 'Unknown error'));
        toast.error('Failed to get analysis from JIM');
      }
    } catch (error) {
      console.error('Error calling LLM service:', error);
      setLlmResponse(`
        <div class="error-response">
          <h4>❌ Service Error</h4>
          <p>${error.message}</p>
          <p><strong>Troubleshooting steps:</strong></p>
          <ul>
            <li>Ensure the LLM backend is running on port 5000</li>
            <li>Check that Supabase connection is working</li>
            <li>Verify OpenAI API key is configured (for semantic search)</li>
          </ul>
        </div>
      `);
      toast.error('Failed to connect to JIM service');
    } finally {
      setIsLoading(false);
    }
  };

  const saveToEnergyAnalyses = async (question, answer) => {
    try {
      // First, check if we need to delete old records
      const { count } = await supabase
        .from('energy_analyses')
        .select('*', { count: 'exact' });

      if (count >= maxRecords) {
        // Delete oldest records to maintain the limit
        const { data: oldRecords } = await supabase
          .from('energy_analyses')
          .select('id')
          .order('created_at', { ascending: true })
          .limit(count - maxRecords + 1);

        const idsToDelete = oldRecords.map(record => record.id);
        await supabase
          .from('energy_analyses')
          .delete()
          .in('id', idsToDelete);
      }

      // Get the most recent meter_id from readings
      const { data: recentReading } = await supabase
        .from('readings')
        .select('meter_id')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();

      const meterId = recentReading ? recentReading.meter_id : "SMR-43563-2-A";

      // Insert new record - FIXED: use 'question' not 'questions'
      const { error } = await supabase
        .from('energy_analyses')
        .insert([
          {
            meter_id: meterId,
            question: question,
            response: answer,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving to energy_analyses:', error);
    }
  };

  // Handle similar question click
  const handleSimilarQuestionClick = (similarQuestion) => {
    setQuestion(similarQuestion);
    // Auto-trigger search after a brief delay
    setTimeout(() => {
      handleAskJim();
    }, 500);
  };

  // Debug component for development
  const DebugInfo = () => {
    if (process.env.NODE_ENV === 'development') {
      return (
        <div style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          margin: '10px 0', 
          fontSize: '12px',
          border: '1px solid #ddd'
        }}>
          <strong>Debug Info:</strong>
          <div>Meter Data Points: {meterData.length}</div>
          <div>Available Meters: {availableMeters.join(', ')}</div>
          <div>Configured Meters: {Object.keys(METER_CONFIG).join(', ')}</div>
          <div>First Data Point: {JSON.stringify(meterData[0])}</div>
          <div>Last Data Point: {JSON.stringify(meterData[meterData.length - 1])}</div>
          <div>All Meter Keys: {meterData.length > 0 ? Object.keys(meterData[0]).join(', ') : 'No data'}</div>
          {searchInfo && (
            <div>
              Search Type: {searchInfo.type} | 
              Similar Questions: {searchInfo.similarQuestions.length}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Search Type Badge Component
  const SearchTypeBadge = () => {
    if (!searchInfo) return null;
    
    const getBadgeColor = () => {
      switch (searchInfo.type) {
        case 'vector_search': return 'success';
        case 'keyword_search': return 'warning';
        default: return 'info';
      }
    };
    
    const getBadgeText = () => {
      switch (searchInfo.type) {
        case 'vector_search': return '🤖 Vector Semantic Search';
        case 'keyword_search': return '🔍 Enhanced Keyword Search';
        default: return '🤖 AI Analysis';
      }
    };

    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          label={getBadgeText()} 
          color={getBadgeColor()} 
          size="small" 
          variant="outlined"
        />
        {searchInfo.similarQuestions.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Found {searchInfo.similarQuestions.length} similar analyses
          </Typography>
        )}
      </Box>
    );
  };

  // Similar Questions Component
  const SimilarQuestionsPanel = () => {
    if (!similarQuestions || similarQuestions.length === 0) return null;

    return (
      <Card sx={{ mt: 2, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🔍 Similar Previous Questions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            JIM found these related analyses in the knowledge base:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {similarQuestions.map((question, index) => (
              <Chip
                key={index}
                label={question.length > 60 ? question.substring(0, 60) + '...' : question}
                onClick={() => handleSimilarQuestionClick(question)}
                variant="outlined"
                size="small"
                sx={{ 
                  justifyContent: 'flex-start',
                  height: 'auto',
                  py: 1,
                  '& .MuiChip-label': { whiteSpace: 'normal' }
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Ad content data
  const rightColumnAds = [
    {
      id: 1,
      title: "Home Battery Systems",
      type: "video",
      description: "Store excess energy with our compact, high-capacity home batteries and accessories.",
      media:"https://goods-vod-eu.kwcdn.com/local-goods-vod/f17793572598e526aef7d74d7b03f277a1e56ea6.f30.mp4"
    },
    {
      id: 2,
      title: "Next-Gen Solar Panels",
      type: "image",
      description: "40% more efficient than conventional panels with our patented nano-coating technology",
      media:"https://goods-vod.kwcdn.com/goods-video/991226355a5aed619dd7b4e8c443e418184d438b.f30.mp4"
    },
    {
      id: 3,
      title: "Interactive Ad Space Available",
      type: "interactive",
      description: "Engage users with rich interactive content. These ads allow for user interaction directly within the ad unit, providing higher engagement and better conversion rates.",
       media:""
    },
    {
      id: 4,
      title: "Brand Story Ad Space Available",
      type: "story",
      description: "Tell your brand story with our immersive full-width ad format. Combine images, text and video to create an engaging narrative about your company or products.",
       media:""
    }
  ];

  return (
    <div className="app-container">
      {/* Navigation */}
      <ToastContainer position="top-right" autoClose={3000} />
      <nav className="navbar">
        <div className="navbar-brand">
          <h2 style={{ fontWeight: "bold", color: "green" }}><a href="/">SPYDER</a></h2>
        </div>
        <div className="navbar-auth">
          <SignedOut>
            <SignInButton mode="modal" />
          </SignedOut>
          <SignedIn>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>
         <div>
          <Link to="/newsletter" className="crumbtrail"><small>Newsletter</small></Link> 
        </div>
      </nav>

      <div className="advertising-header">
        <h1>Newsletter</h1>
      </div>
      
      {/* Meter Visualizations - Added above the newsletter content */}
      <Container maxWidth="lg" className="meters-container">
        <DebugInfo />
        <Box className="meters-section">
          <Typography variant="h4" gutterBottom className="section-title" sx={{ textAlign: 'center', mb: 4 }}>
            Real-Time Energy Monitoring
          </Typography>
          
          {graphsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading meter data...
              </Typography>
            </Box>
          ) : availableMeters.length === 0 ? (
            <Alert severity="info">
              No meter data available. Please check your Supabase connection and data.
            </Alert>
          ) : (
            <Grid container spacing={3} className="meters-grid">
              {/* Show configured meters dynamically based on what we have data for */}
              {Object.keys(METER_CONFIG).slice(0, 3).map((meterId) => {
                // Check if this meter has any data
                const hasData = meterData.some(item => item[meterId] !== null);
                
                console.log(`Meter ${meterId} has data:`, hasData);
                
                return (
                  <Grid item xs={12} md={4} key={meterId}>
                    <Paper 
                      elevation={3} 
                      className="meter-paper"
                      sx={{ 
                        p: 2, 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: hasData ? '1px solid #e0e0e0' : '2px dashed #ff6b6b'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        className="meter-title"
                        sx={{ 
                          textAlign: 'center',
                          color: getMeterColor(meterId),
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      >
                        {getMeterDisplayName(meterId)}
                        {!hasData && (
                          <Typography variant="caption" display="block" color="error">
                            (No data available)
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }}>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart 
                            data={meterData} 
                            margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="time" 
                              tick={{ fontSize: 10 }}
                              interval="preserveStartEnd"
                            />
                            <YAxis 
                              tick={{ fontSize: 10 }}
                              domain={['auto', 'auto']}
                              width={35}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Line 
                              type="monotone" 
                              dataKey={meterId}
                              stroke={getMeterColor(meterId)}
                              strokeWidth={3}
                              dot={false}
                              activeDot={false}
                              connectNulls={true}
                              isAnimationActive={true}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* ENHANCED: LLM Query Section with Semantic Search */}
        <Card className="llm-section" sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom className="llm-title">
              Ask <span className="ask-jim">Jim</span> - Energy Analytics AI
            </Typography>
            
            <Box className="key-features">
              <Typography variant="body2" color="text.secondary">
                <strong>Enhanced with Semantic Search:</strong>
              </Typography>
              <ul>
                <li>✅ Vector Semantic Search across knowledge base</li>
                <li>✅ Context-aware responses using previous analyses</li>
                <li>✅ Pattern recognition and trend analysis</li>
                <li>✅ Cost optimization recommendations</li>
                <li>✅ Real-time data integration</li>
              </ul>
            </Box>

            <Grid container spacing={2} alignItems="flex-end" className="query-input">
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  label="Ask JIM about your energy data, trends, or optimization"
                  variant="outlined"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isLoading) handleAskJim();
                  }}
                  disabled={isLoading}
                  className="question-field"
                  multiline
                  minRows={2}
                  maxRows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '1.1rem',
                      backgroundColor: 'white',
                      padding: '0 4px',
                      borderRadius: '4px',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#1976d2',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '16px',
                      lineHeight: '1.5',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  onClick={handleAskJim}
                  disabled={isLoading || !question.trim()}
                  fullWidth
                  className="ask-jim-btn"
                  size={isMobile ? "medium" : "large"}
                  sx={{
                    height: '56px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                  }}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isLoading ? 'Analyzing...' : 'Ask JIM'}
                </Button>
              </Grid>
            </Grid>

            {/* Search Type Information */}
            {searchInfo && <SearchTypeBadge />}

            {/* Similar Questions Panel */}
            <SimilarQuestionsPanel />

            {/* LLM Response Area */}
            {llmResponse && (
              <Box className="llm-response-container" sx={{ mt: 2 }}>
                <Typography variant="h6" className="response-title" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🤖 JIM's Analysis
                </Typography>
                <Box 
                  className="llm-response enhanced-response"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    bgcolor: 'background.paper'
                  }}
                >
                  <div 
                    id="llm-response"
                    dangerouslySetInnerHTML={{ __html: llmResponse }}
                  />
                </Box>
              </Box>
            )}

            {/* Sample Questions for Testing */}
            {!llmResponse && !isLoading && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  💡 Try these sample questions to test semantic search:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {[
                    "Show me consumption trends",
                    "What are cost saving opportunities?",
                    "Analyze peak hour usage",
                    "Compare meter performance",
                    "Any anomalies in the data?"
                  ].map((sample, index) => (
                    <Chip
                      key={index}
                      label={sample}
                      onClick={() => setQuestion(sample)}
                      variant="outlined"
                      size="small"
                      clickable
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      <p className="join-campaign"><a href="/pricing">How do I join the campaign?</a></p>

      <div className="main-content">
        <div className="newsletter-hero">
          <h2>Stay Informed on Energy Tariffs</h2>
          <p>Join our newsletter to receive the latest updates on energy prices, market trends, and money-saving tips directly to your inbox.</p>
        </div>
        
        <div className="newsletter-content">
          <div className="editorial-content">
            <div className="editorial-left">
              <h3>Ads</h3>
              

              <div className="ad-space">
                <h4>Sponsored Articles</h4>
                <ul>
                  <li><a href="#">Race to Net Zero</a></li>
                </ul>
              </div>
              
              <div className="ad-space">
                <h4>Sponsored Links</h4>
                <ul>
                  <li><a href="https://www.neso.energy/"><small>NESO</small></a></li>
                  <li><a href="https://www.lowcarboncontracts.uk/">Low Carbon Contracts</a></li>
                </ul>
              </div>

              <div className="ad-space">
                <br />
                <h4>Clean Energy</h4>
                <div>
                  <video 
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster="/images/green-energy-poster.jpg"
                    className="brand-video"
                  >
                    <source src="https://goods-vod.kwcdn.com/goods-video/2e893b7d49267bbcf912d457f659eede65dad719.f30.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <p>How we're powering tomorrow's world today</p>
                </div>
              </div>
              
              <div className="ad-space">
                <h4>Sponsored Content</h4>
                <p>Native-style sponsored articles within our newsletter</p>
              </div>
              
              <div className="ad-space">
                <h5 className="available-space">SPACE AVAILABLE</h5>
              </div>
            </div>
            
            <div className="editorial-right">
              <h3>Why Subscribe to Our Newsletter?</h3>
              <p>Our newsletter transforms how you engage with the energy market, providing exclusive insights and opportunities not available to regular visitors.</p>
              
              <div className="inline-signup-form">
                <h4>Subscribe to Our Newsletter</h4>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name</label>
                      <input 
                        type="text" 
                        id="name" 
                        {...register("name", { required: "Name is required" })}
                      />
                      {errors.name && <span className="error">{errors.name.message}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <input 
                        type="email" 
                        id="email" 
                        {...register("email", { 
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                      />
                      {errors.email && <span className="error">{errors.email.message}</span>}
                    </div>
                  </div>
                  
                  <div className="consent-checkbox">
                    <input 
                      type="checkbox" 
                      id="consent" 
                      {...register("consent", { 
                        required: "You must agree to receive emails" 
                      })}
                    />
                    <label htmlFor="consent">
                      I agree to receive newsletter emails containing news, updates, and promotional offers. 
                      I understand I can unsubscribe at any time.
                    </label>
                    {errors.consent && <span className="error">{errors.consent.message}</span>}
                  </div>
                  
                  <button type="submit" className="submit-btn">Subscribe Now</button>
                </form>
                
                <div className="privacy-note">
                  <p>We respect your privacy and will never share your information with third parties. You can unsubscribe at any time.</p>
                </div>
              </div>
              
              <h4>Ads</h4> 
              <div className="ad-models">
                <div className="featured-grid">
                  {rightColumnAds.map(ad => (
                    <div key={ad.id} className="featured-card">
                      <div className="featured-header">
                        <span className="featured-title">{ad.title}</span>
                      </div>
                     
                      <div className="feature">
                        <div className="feature-video-container">
                          <video 
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="feature-video"
                          >
                          <source src={ad.media} type="video/mp4" />
                         
                          </video>
                        </div>
                        <h4>Home Battery Systems</h4>
                        <p>Store excess energy with our compact, high-capacity home batteries</p>
                      </div>

                      <div className="featured-footer">
                        <p className="featured-description">{ad.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="newsletter-ad">
                <h4>Special Offer for Newsletter Subscribers</h4>
                <p>Subscribe today and get our exclusive guide "5 Energy Saving Secrets That Could Cut Your Bills by 20%" absolutely free!</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="newsletter-features">
          <h3>What You'll Receive</h3>
          <div className="features-grid">
            <div className="feature">
              <h4>Weekly Market Updates</h4>
              <p>Stay informed about the latest energy price fluctuations and market trends.</p>
            </div>
            <div className="feature">
              <h4>Exclusive Deals</h4>
              <p>Get access to special offers and promotions not available to the general public.</p>
            </div>
            <div className="feature">
              <h4>Energy Saving Tips</h4>
              <p>Learn practical ways to reduce your energy consumption and save money.</p>
            </div>
            <div className="feature">
              <h4>Industry Insights</h4>
              <p>Gain valuable knowledge about the energy sector and future developments.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
